// File: src/App.js

import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";

// Page and Component Imports
import SignInPage from "./pages/SignInPage.js";
import OnboardingPage from "./pages/OnboardingPage.js";
import HomePage from "./pages/HomePage";
import TrendsPage from "./pages/TrendsPage.js";
import BroadcastPage from "./pages/RadioPage";
import ChartsPage from "./pages/ChartsPage.js";
import FansPage from "./pages/FansPage.js";
import UserProfilePage from './pages/Profile/UserProfilePage';
import PublicProfilePage from './pages/Profile/PublicProfilePage.js';
import HandleRedirectPage from './pages/HandleRedirectPage';
import MusicUploadPage from "./pages/MusicUploadPage";
import MyDownloads from "./pages/MyDownloads";
import ArtistAccountForm from "./pages/ArtistAccountForm";
import FullMusicPlayer from "./pages/FullMusicPlayer";
import MiniMusicPlayer from "./components/music/MiniMusicPlayer";
import PlaylistDetailPage from './pages/PlaylistDetailPage'; 
import AlbumDetailPage from './pages/AlbumDetailPage'; 
import AuthTestComponent from "./components/AuthTestComponent.js";
import TippingCancelledPage from "./pages/TippingCancelledPage.js";
import FrostedGlassBackdrop from "./components/FrostedGlassBackdrop";
import TopMenuBar from "./layouts/TopMenuBar";
import BottomMenuBar from "./layouts/BottomMenuBar";
import { Toaster, toast } from 'react-hot-toast'; 
import SearchResults from "./components/search/SearchResults.js";
import ArtistDashboard from './pages/artistDashboard/ArtistDashboard';
import PostDetailPage from './pages/PostDetailPage';
import FeedPage from './pages/FeedPage';
import SettingsPage from './pages/Settings/SettingsPage.js';
import AddPaymentMethodPage from './pages/AddPaymentMethodPage';
import SongPage from './pages/SongPage';
import TermsPage from './pages/TermsPage';
import AdminRoute from './components/AdminRoute';
import AdminPage from './pages/AdminPage';
import CopyrightPage from './pages/CopyrightPage';
import CopyrightPolicyPage from './pages/CopyrightPolicyPage';
import PrivacyPage from './pages/PrivacyPage';
import ProtectedRoute from "./components/miscellaneous/ProtectedRoute";
import OfflinePlaybackPage from "./pages/OfflinePlaybackPage";


import TermsUpdateModal from "./components/miscellaneous/TermsUpdateModal";

// Context and Service Imports
import { MusicProvider, useMusic } from "./context/MusicContext.js";
import { ProfileProvider, useProfile } from "./context/ProfileContext";
import { AnimationProvider } from './context/AnimationContext';
import { useSearch, SearchProvider } from './context/SearchContext';

// Other imports
import { AnimatePresence } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import "./App.css";
import AddToPlaylistModal from './components/music/AddToPlaylistModal';
import CreatePlaylistModal from './components/music/CreatePlaylistModal';

// Chart.js imports & registrations
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const STRIPE_PUBLISHABLE_KEY = 'pk_test_YOUR_STRIPE_TEST_PUBLISHABLE_KEY';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const RouteHandler = () => {
  const location = useLocation();
  const { user, userProfile, activeProfile, setFeedReturnPath, isDarkMode } = useProfile();
  const { currentSong, isExpanded, setIsExpanded, setReturnToPath, isMicroPlayer } = useMusic();
  const { results, isLoading: isSearchLoading, isSearchVisible, clearSearch } = useSearch();
  const isOnSongRoute = location.pathname.startsWith('/song/');
  
  const [selectedSong, setSelectedSong] = useState(null);
  const [isAddToPlaylistOpen, setIsAddToPlaylistOpen] = useState(false);
  const [isCreatePlaylistOpen, setIsCreatePlaylistOpen] = useState(false);

  const handleOpenPlaylistModal = (song) => {
    if (activeProfile?.type === 'artist') {
      toast.error("Artist accounts cannot add songs to playlists.");
      return;
    }
    setSelectedSong(song);
    setIsAddToPlaylistOpen(true);
  };
  
  const handleNavigateToCreate = () => {
    setIsAddToPlaylistOpen(false);
    setIsCreatePlaylistOpen(true);
  };

  useEffect(() => {
    const isOnSongPage = location.pathname.startsWith('/song/');
    const isOnFeedPage = location.pathname.startsWith('/feed');
    const isOnSettingsPage = location.pathname.startsWith('/settings');
    const isOnDashboardPage = location.pathname.startsWith('/dashboard');
    
    if (!isOnSongPage && !isOnFeedPage && !isOnSettingsPage && !isOnDashboardPage) {
        setReturnToPath(location.pathname);
        setFeedReturnPath(location.pathname);
    }
    if (isExpanded !== isOnSongPage) {
        setIsExpanded(isOnSongPage);
    }
  }, [location.pathname, isExpanded, setIsExpanded, setReturnToPath, setFeedReturnPath]);

  const fullScreenRoutes = ['/home', '/trending'];
  const isFullScreen = fullScreenRoutes.includes(location.pathname);

  const isGuest = user && user.isAnonymous;
  const isRegisteredAndOnboarded = userProfile && userProfile.isOnboarded === true;
  const isOnboardingPage = location.pathname === '/onboarding';

  const shouldRenderMenus = (isGuest || isRegisteredAndOnboarded) && !isOnboardingPage;

  const showBottomMenuRoutes = [
      "/",
      "/home", 
      "/trends",
      "/radio", 
      "/fans",
      "/charts",
      "/artist", 
      "/profile", 
      "/playlist", 
      "/album",
      "/trending", 
      "/dashboard", 
      "/upload-music",
      "/my-downloads",
      "/feed",
      "/terms",
      "/copyright",
      "/privacy",
      "/settings",
      "/add-payment-method"
  ];
  const showBottomMenu = shouldRenderMenus && showBottomMenuRoutes.some(path => location.pathname.startsWith(path));

  const miniPlayerAllowedRoutes = [
      "/home",
      "/fans", 
      "/charts",
      "/artist", 
      "/profile", 
      "/playlist", 
      "/feed",
      "/album",
      "/my-downloads", 
      "/settings",
      "/radio",
      "/terms",
      "/copyright",
      "/privacy",
      "/dashboard",
      "/offline-playback",
  ];

  const showMiniPlayer = shouldRenderMenus && currentSong && !isExpanded && miniPlayerAllowedRoutes.some(path => location.pathname.startsWith(path));

  let paddingBottomClass = "";
  if (showBottomMenu && showMiniPlayer) {
    paddingBottomClass = "pb-40"; 
  } else if (showBottomMenu) {
    paddingBottomClass = "pb-24";
  } else if (showMiniPlayer) {
    paddingBottomClass = "pb-20";
  }

  const finalPaddingClass = isFullScreen 
    ? '' 
    : `pt-16 ${paddingBottomClass}`;

  const routeKey = location.pathname.split('/')[1] || '/';

  return (
    <div className={`relative min-h-screen flex flex-col transition-colors duration-500 ${
      isDarkMode ? 'dark bg-brand-black text-brand-white' : 'bg-brand-white text-brand-black'
    }`}>

      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      
      {/* ✅ 2. Render Legacy Terms Intercept Modal here */}
      <TermsUpdateModal />

      {showBottomMenu && <TopMenuBar />}

      {isSearchVisible && (
        <div 
          onClick={clearSearch} 
          className="fixed inset-0 top-16 bg-black/40 z-[60] backdrop-blur-xs"
        >
          <div 
            onClick={(e) => e.stopPropagation()} 
            className={`p-4 max-w-lg mx-auto rounded-b-[2rem] shadow-2xl border-x border-b transition-colors duration-300 ${
              isDarkMode 
                ? "bg-[#1a1a1a] border-white/5" 
                : "bg-white border-slate-100"
            }`}
          >
            <SearchResults results={results} isLoading={isSearchLoading} />
          </div>
        </div>
      )}

      <main className={`relative overflow-x-hidden flex-grow ${finalPaddingClass}`}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={routeKey}> 
            <Route path="/login" element={<SignInPage />} />
            <Route path="/" element={<Navigate to="/home" replace />} />

            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

            <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/trends" element={<ProtectedRoute><TrendsPage onOpenPlaylistModal={handleOpenPlaylistModal} /></ProtectedRoute>} />
            <Route path="/trending" element={<ProtectedRoute><TrendsPage onOpenPlaylistModal={handleOpenPlaylistModal} /></ProtectedRoute>} />
            <Route path="/radio" element={<ProtectedRoute><BroadcastPage onOpenPlaylistModal={handleOpenPlaylistModal} /></ProtectedRoute>} />
            <Route path="/fans" element={<ProtectedRoute><FansPage /></ProtectedRoute>} /> 
            <Route path="/charts" element={<ProtectedRoute><ChartsPage /></ProtectedRoute>} />
            <Route path="/auth-test" element={<ProtectedRoute><AuthTestComponent /></ProtectedRoute>} />
            <Route path="/profile/" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
            <Route path="/profile/:profileId" element={<ProtectedRoute><PublicProfilePage /></ProtectedRoute>} />
            <Route path="/:handle" element={<ProtectedRoute><HandleRedirectPage /></ProtectedRoute>} />
            <Route path="/upload-music" element={<ProtectedRoute><MusicUploadPage /></ProtectedRoute>} />
            <Route path="/song/:songId" element={<ProtectedRoute><SongPage /></ProtectedRoute>} />
            <Route path="/music-player" element={<ProtectedRoute><SongPage /></ProtectedRoute>} />
            <Route path="/playlist/:playlistId" element={<ProtectedRoute><PlaylistDetailPage /></ProtectedRoute>} />
            <Route path="/album/:albumId" element={<ProtectedRoute><AlbumDetailPage /></ProtectedRoute>} />
            <Route path="/feed/:postId?" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
            <Route path="/feed/*" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/add-payment-method" element={<ProtectedRoute><AddPaymentMethodPage /></ProtectedRoute>} />
            <Route path="/my-downloads" element={<ProtectedRoute><OfflinePlaybackPage /></ProtectedRoute>} />
            <Route path="/offline-playback" element={<ProtectedRoute><OfflinePlaybackPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><ArtistDashboard /></ProtectedRoute>} />
            <Route path="/artist-account-form" element={<ProtectedRoute><ArtistAccountForm /></ProtectedRoute>} />
            <Route path="/tipping-cancelled/:songId" element={<ProtectedRoute><TippingCancelledPage /></ProtectedRoute>} />
            <Route path="/terms" element={<ProtectedRoute><TermsPage /></ProtectedRoute>} />
            <Route path="/copyrightpolicy" element={<ProtectedRoute><CopyrightPolicyPage /></ProtectedRoute>} />
            <Route path="/copyright" element={<ProtectedRoute><CopyrightPage /></ProtectedRoute>} />
            <Route path="/privacy" element={<ProtectedRoute><PrivacyPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminRoute><AdminPage /></AdminRoute></ProtectedRoute>} />
            
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </AnimatePresence>
      </main>

      <div className="fixed bottom-0 left-0 right-0 z-40 pt-8 pointer-events-none">
        <AnimatePresence>
          {showMiniPlayer && !isMicroPlayer && (
            <FrostedGlassBackdrop />
          )}
        </AnimatePresence>

        <div className="relative pointer-events-auto">
          {showMiniPlayer && (
            <MiniMusicPlayer onOpenPlaylistModal={handleOpenPlaylistModal} />
          )}
          {showBottomMenu && <BottomMenuBar userProfile={activeProfile} />}
        </div>
      </div>

      <AnimatePresence>
        {(isOnSongRoute || isExpanded) && <FullMusicPlayer />}
      </AnimatePresence>

      <AddToPlaylistModal
        isOpen={isAddToPlaylistOpen}
        onClose={() => setIsAddToPlaylistOpen(false)}
        songToAdd={selectedSong}
        onNavigateToCreate={handleNavigateToCreate}
      />

      <CreatePlaylistModal
        isOpen={isCreatePlaylistOpen}
        onClose={() => setIsCreatePlaylistOpen(false)}
        songToAdd={selectedSong}
        source="mini_player"
      />
    </div>
  );
};

const AppContent = () => {
  const { isLoading, activeProfile } = useProfile();

  if (isLoading || !activeProfile) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f3f4f6' }}>
        <p style={{ fontSize: '1.2rem', color: '#4b5563' }}>Loading...</p>
      </div> 
    );
  }

  return (
    <RouteHandler />
  );
};

const App = () => {
  return (
    <AppContent />
  );
};

export default App;